import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Sparkles, AlertTriangle, ShieldCheck, CloudSun, Users, Camera, Radar } from "lucide-react";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import PublicHeader from "@/components/PublicHeader";
import { useMeta } from "@/lib/useMeta";
import { DEMOS, DEMO_SLUGS } from "./demoData";

const GOLD = "#C9A84C";
const TONE = { green: "#00C896", amber: "#F59E0B", red: "#EF4444" };
const BADGE = { "On Track": "#00C896", Delayed: "#F59E0B", Critical: "#EF4444" };

const inr = (cr) => `₹${cr.toLocaleString("en-IN")} Cr`;
const money = (v) => `₹${(v / 10000000).toFixed(2)} Cr`;
const num = (n) => n.toLocaleString("en-IN");
const dt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

const Card = ({ children, className = "", testId }) => (
  <div className={`bg-[#112240] border border-white/10 rounded-lg ${className}`} data-testid={testId}>{children}</div>
);

const Ring = ({ pct }) => {
  const r = 42, c = 2 * Math.PI * r;
  return (
    <div className="relative w-28 h-28 shrink-0" data-testid="progress-ring">
      <svg viewBox="0 0 100 100" className="w-28 h-28">
        <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="8" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={GOLD} strokeWidth="8" strokeLinecap="round"
                strokeDasharray={`${(c * pct) / 100} ${c}`} transform="rotate(-90 50 50)" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-xl font-bold" data-testid="ring-value">{pct}%</span>
    </div>
  );
};

export default function DemoPage() {
  const { slug } = useParams();
  const d = DEMOS[slug];
  useMeta({ title: d?.meta.title, description: d?.meta.description, path: `/demo/${slug}` });
  if (!d) return <Navigate to="/features" replace />;
  const { project: p, dpr } = d;

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid={`demo-page-${slug}`}>
      <PublicHeader />

      <div className="sticky top-14 md:top-16 z-30 flex flex-wrap items-center gap-3 px-5 sm:px-8 py-3" style={{ backgroundColor: GOLD }} data-testid="demo-banner">
        <Sparkles className="w-4 h-4 text-[#0A1628] shrink-0" />
        <span className="text-[13px] font-semibold text-[#0A1628]">
          You are viewing a demo project. Sign up free to create your own.
        </span>
        <Link to="/signup" className="sm:ml-auto inline-flex items-center h-9 px-4 rounded-lg bg-[#0A1628] text-white text-[13px] font-semibold hover:bg-[#112240] transition-colors duration-200" data-testid="demo-banner-trial-btn">
          Start Free Trial <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Link>
      </div>

      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-10 space-y-10">
        <Card className="p-6" testId="demo-project-overview">
          <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: GOLD }}>{d.sector} — sample project</span>
          <div className="flex flex-col lg:flex-row gap-8 mt-4">
            <div className="flex-1">
              <h1 className="font-display text-2xl sm:text-3xl font-bold" data-testid="demo-project-name">{p.name}</h1>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-5 text-[13px]">
                {[["Client", p.client], ["Contract value", inr(p.value)], ["Location", p.location], ["Project type", p.type],
                  ["Planned completion", dt(p.plannedFinish)], ["Forecast completion", dt(p.forecastFinish)]].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[10.5px] uppercase tracking-wider text-white/45">{k}</div>
                    <div className="mt-0.5">{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-5">
              <Ring pct={p.overall} />
              <div className="text-[12px] space-y-2">
                <div><span className="text-white/45">Planned</span><div className="num text-base">{p.plannedOverall}%</div></div>
                <div><span className="text-white/45">Actual</span><div className="num text-base" style={{ color: GOLD }}>{p.overall}%</div></div>
                <div><span className="text-white/45">Risk</span>
                  <div className="num text-base" style={{ color: TONE[p.risk.toLowerCase()] }}>{p.risk}</div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <section data-testid="demo-wbs">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em]" style={{ color: GOLD }}>WBS tracker</h2>
          <Card className="mt-4 overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-[10.5px] uppercase tracking-wider text-white/45 border-b border-white/10">
                <tr>{["Code", "Activity", "Unit", "Scope", "Executed", "Planned %", "Actual %", "Status", "Responsible"].map((h) => (
                  <th key={h} className="text-left font-medium px-3 py-2.5 whitespace-nowrap">{h}</th>))}</tr>
              </thead>
              <tbody>
                {d.acts.map((a) => (
                  <tr key={a.id} className="border-b border-white/5" data-testid={`demo-activity-${a.id}`}>
                    <td className="num px-3 py-2.5">{a.code}</td>
                    <td className="px-3 py-2.5">{a.name}</td>
                    <td className="px-3 py-2.5 text-white/55">{a.unit}</td>
                    <td className="num px-3 py-2.5 text-right">{num(a.qty)}</td>
                    <td className="num px-3 py-2.5 text-right">{num(a.done)}</td>
                    <td className="num px-3 py-2.5 text-right text-white/60">{a.planned}%</td>
                    <td className="num px-3 py-2.5 text-right" style={{ color: GOLD }}>{a.actual}%</td>
                    <td className="px-3 py-2.5">
                      <span className="text-[10.5px] px-2 py-0.5 rounded" style={{ color: BADGE[a.status], background: `${BADGE[a.status]}1a` }}>{a.status}</span>
                    </td>
                    <td className="px-3 py-2.5 text-white/60 whitespace-nowrap">{a.owner}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </section>

        <section data-testid="demo-boq">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em]" style={{ color: GOLD }}>BOQ summary</h2>
          <Card className="mt-4 overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-[10.5px] uppercase tracking-wider text-white/45 border-b border-white/10">
                <tr>{["Description", "Unit", "Planned qty", "Actual qty", "Rate", "Total value", "Variance"].map((h) => (
                  <th key={h} className="text-left font-medium px-3 py-2.5 whitespace-nowrap">{h}</th>))}</tr>
              </thead>
              <tbody>
                {d.boq.map((b) => (
                  <tr key={b.id} className="border-b border-white/5" data-testid={`demo-boq-${b.id}`}>
                    <td className="px-3 py-2.5">{b.desc}</td>
                    <td className="px-3 py-2.5 text-white/55">{b.unit}</td>
                    <td className="num px-3 py-2.5 text-right">{num(b.plannedQty)}</td>
                    <td className="num px-3 py-2.5 text-right">{num(b.actualQty)}</td>
                    <td className="num px-3 py-2.5 text-right">₹{num(b.rate)}</td>
                    <td className="num px-3 py-2.5 text-right">{money(b.actualValue)}</td>
                    <td className="num px-3 py-2.5 text-right font-medium" style={{ color: "#F59E0B" }} data-testid={`demo-variance-${b.id}`}>
                      {b.variancePct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </section>

        <section data-testid="demo-risk-radar">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em]" style={{ color: GOLD }}>Risk Radar preview</h2>
            <span className="inline-flex items-center gap-1.5 text-[10.5px] border px-2 py-0.5 rounded"
                  style={{ borderColor: "rgba(201,168,76,0.45)", color: GOLD }} data-testid="demo-risk-plan-label">
              <Radar className="w-3 h-3" /> AI-Powered Risk Analysis — Available on Professional Plan
            </span>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {d.risks.map((r, i) => (
              <Card key={r.title} className="p-5 border-t-2" testId={`demo-risk-${r.level}`}>
                <div className="flex items-center gap-2" style={{ color: TONE[r.level] }}>
                  {r.level === "green" ? <ShieldCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  <span className="text-[10.5px] uppercase tracking-wider font-semibold">
                    {r.level === "green" ? "Low risk" : r.level === "amber" ? "Medium risk" : "Critical"}
                  </span>
                </div>
                <h3 className="font-semibold text-[14px] mt-3">{r.title}</h3>
                <p className="text-[12.5px] text-white/60 mt-2 leading-relaxed">{r.body}</p>
                <div className="mt-4 pt-3 border-t border-white/10 space-y-2 text-[12px]">
                  <div><span className="text-white/45">Recommended mitigation: </span>{r.mitigation}</div>
                  <div><span className="text-white/45">Forecast impact: </span><span style={{ color: TONE[r.level] }}>{r.impact}</span></div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        <section className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
          <div data-testid="demo-dpr">
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em]" style={{ color: GOLD }}>DPR snapshot</h2>
            <Card className="mt-4 p-5">
              <div className="flex flex-wrap gap-x-8 gap-y-3 text-[12.5px]">
                <div><div className="text-[10.5px] uppercase tracking-wider text-white/45">Date</div><div className="num mt-0.5">{dt(dpr.date)}</div></div>
                <div><div className="text-[10.5px] uppercase tracking-wider text-white/45">Overall progress</div><div className="num mt-0.5" style={{ color: GOLD }}>{dpr.progress}%</div></div>
                <div><div className="text-[10.5px] uppercase tracking-wider text-white/45">Activities completed</div><div className="num mt-0.5">{dpr.completedToday}</div></div>
                <div><div className="text-[10.5px] uppercase tracking-wider text-white/45 flex items-center gap-1"><CloudSun className="w-3 h-3" /> Weather</div><div className="mt-0.5">{dpr.weather}</div></div>
                <div><div className="text-[10.5px] uppercase tracking-wider text-white/45 flex items-center gap-1"><Users className="w-3 h-3" /> Workforce</div><div className="num mt-0.5">{num(dpr.workforce)}</div></div>
              </div>
              <div className="mt-5 grid sm:grid-cols-[180px_1fr] gap-4">
                <div className="h-[120px] bg-white/5 border border-white/10 rounded flex flex-col items-center justify-center gap-2 text-white/35" data-testid="demo-site-photo">
                  <Camera className="w-6 h-6" />
                  <span className="text-[10.5px]">Site photo — {dt(dpr.date)}</span>
                </div>
                <p className="text-[12.5px] text-white/70 leading-relaxed">{dpr.narrative}</p>
              </div>
            </Card>
          </div>

          <div data-testid="demo-whatsapp">
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em]" style={{ color: GOLD }}>WhatsApp alert</h2>
            <div className="mt-4 mx-auto w-[290px] rounded-[28px] border-[6px] border-[#112240] bg-[#0b141a] shadow-2xl shadow-black/60 overflow-hidden">
              <div className="bg-[#1f2c34] px-4 py-2.5 text-[12px] text-white/85">Plan-Sarthi Alerts</div>
              <div className="p-3">
                <div className="bg-[#005c4b] rounded-lg rounded-tr-none p-3 text-[11.5px] leading-relaxed text-white/95 whitespace-pre-line" data-testid="whatsapp-message">
{`*Plan-Sarthi DPR Alert*

Project: ${p.name}
Date: ${dt(dpr.date)}

Today's Progress: ${dpr.progress}% achieved vs ${p.plannedOverall}% planned
Activities Completed: ${dpr.completedToday}
Activities Delayed: ${d.acts.filter((a) => a.status !== "On Track").length}
Risk Status: ${p.risk}

Overall Completion: ${p.overall}%
Forecasted Completion: ${dt(p.forecastFinish)}

View Full Report: plansarthi.com

— Plan-Sarthi by Sarthi Associates`}
                  <div className="text-right text-[9.5px] text-white/50 mt-2">18:00 ✓✓</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border rounded-lg p-7 sm:p-9 text-center" style={{ borderColor: "rgba(201,168,76,0.35)", background: "rgba(201,168,76,0.07)" }} data-testid="demo-upgrade-cta">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">
            Liked what you saw? Start managing your own <span style={{ color: GOLD }}>{d.sector}</span> project today.
          </h2>
          <div className="flex flex-wrap gap-3 justify-center mt-7">
            <Link to="/signup" className="inline-flex items-center h-11 px-6 rounded-lg text-[15px] font-semibold text-white" style={{ backgroundColor: GOLD }} data-testid="demo-cta-trial-btn">
              Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link to="/pricing" className="inline-flex items-center h-11 px-6 rounded-lg border border-white/35 text-[15px] text-white hover:bg-white/10 transition-colors duration-200" data-testid="demo-cta-pricing-btn">
              View Pricing
            </Link>
          </div>
        </section>

        <section data-testid="demo-other-sectors">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em]" style={{ color: GOLD }}>Other sector demos</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-5">
            {DEMO_SLUGS.filter((s) => s !== slug).map((s) => (
              <Link key={s} to={`/demo/${s}`} className="bg-[#112240] border border-white/10 rounded-lg p-3 text-[12px] text-center hover:border-[#C9A84C]/50 transition-colors duration-200" data-testid={`other-demo-${s}`}>
                {DEMOS[s].sector}
              </Link>
            ))}
          </div>
        </section>
      </div>

      <Footer />
      <BackToTop />
    </div>
  );
}
